<template>
  <div>
    <div v-for="(field, name) in fields" :key="field.id" class="field-item">
      <div
        v-if="field.show !== false"
        class="field-item-wrapper"
        :class="[
          field.additionalClass || '',
          { 'single-row': field.isSingleRow },
          `field-item-${field.type}`,
        ]"
      >
        <!--START: Title-->
        <div
          v-if="field.title"
          class="field-title"
          :class="{ 'group-title': field.type == 'group' }"
        >
          <label>{{ field.title }}</label>
          <p>{{ field.description }}</p>
          <div v-if="field.info" class="hidden-info">
            <em>i</em>
            <p>{{ field.info }}</p>
          </div>
        </div>
        <!--END: Title-->

        <!--START: Field-->
        <div class="field-wrapper">
          <!--Group Fields-->
          <GroupFormFields
            v-if="field.type == 'group'"
            class="field-group-wrapper"
            :fields="field.fields"
            :class="{ 'pad-group-fields': field.title }"
            @fieldChanged="fieldChanged()"
          ></GroupFormFields>

          <!--Toggle-->
          <ToggleButton
            v-else-if="field.type == 'toggle'"
            :field="field"
            :toggleName="name"
            @fieldChanged="fieldChanged()"
          ></ToggleButton>

          <!--Dropdown-->
          <Dropdown
            v-else-if="field.type == 'dropdown'"
            :field="field"
            :class="{ error: field.hasError }"
            @fieldChanged="fieldChanged()"
          ></Dropdown>

          <!--Multi-select dropdown-->
          <MultipleSelectDropdown
            v-else-if="field.type == 'multiple-select-dropdown'"
            :field="field"
            :class="{ error: field.hasError }"
            @fieldChanged="fieldChanged()"
          ></MultipleSelectDropdown>

          <!--Textarea-->
          <div v-else-if="field.type == 'textarea'">
            <textarea
              :name="field.name"
              :placeholder="field.placeholder"
              :required="field.required"
              :readonly="field.readOnly != undefined ? field.readOnly : false"
              :class="[{ error: field.hasError }, field.resizeClass]"
              :maxlength="field.maxlength"
              :rows="field.rows || 4"
              spellcheck="false"
              @focus="field.hasError = false"
              @input="fieldChanged()"
              v-model="field.value"
            ></textarea>
          </div>

          <!--Text editor-->
          <div v-else-if="field.type == 'text-editor'" class="text-editor">
            <VueEditor
              :name="field.name"
              :placeholder="field.placeholder"
              :required="field.required"
              :readonly="field.readOnly != undefined ? field.readOnly : false"
              :class="[{ error: field.hasError }, field.resizeClass]"
              :editorToolbar="editorToolbar"
              class="vue-text-editor"
              @focus="field.hasError = false"
              @input="fieldChanged()"
              v-model="field.value"
            />
          </div>

          <!--OTP field-->
          <div v-else-if="field.type == 'otp'">
            <OtpField :field="field" @OTPChanged="OTPChanged"></OtpField>
          </div>

          <!--Text input-->
          <div v-else :class="`type-${field.type}`">
            <!-- Prefix -->
            <div
              v-if="field.prefix"
              class="prefix"
              :style="`width: ${field.prefixCssWidth}`"
            >
              {{ field.prefix }}
            </div>

            <input
              :type="field.type"
              :name="field.name"
              :placeholder="
                field.showEditButton && field.emptyPlaceholder
                  ? field.emptyPlaceholder
                  : field.placeholder
              "
              :min="field.min"
              :max="field.max"
              :required="field.required"
              :value="field.id"
              :readonly="field.readOnly != undefined ? field.readOnly : false"
              :class="[
                { error: field.hasError },
                { 'value-only': field.showEditButton },
                { 'tiny-field': field.isSmallField },
              ]"
              :style="`${
                field.prefix
                  ? `padding-left: calc(${field.prefixCssWidth} + .75rem); width: calc(100% - 2rem - ${field.prefixCssWidth})`
                  : ''
              }`"
              :disabled="field.showEditButton || field.disabled"
              spellcheck="false"
              @focus="fieldFocused(field)"
              @input="fieldChanged()"
              @change="fieldChanged()"
              v-model="field.value"
            />
          </div>
        </div>
        <!--END: Field-->

        <!--START: Alert-->
        <div
          v-if="field.alertMessage != undefined"
          class="alert-message-wrapper"
        >
          <div class="alert-message">
            <div class="alert-notification" :class="field.alertMessage.color">
              <unicon name="bolt"></unicon>
              <span>{{ field.alertMessage.message }}</span>
            </div>
          </div>
        </div>
        <!--END: Alert-->
      </div>
    </div>
  </div>
</template>

<script>
// Components
import ToggleButton from "./ToggleButton";
import Dropdown from "./Dropdown";
import OtpField from "./OtpField";
import MultipleSelectDropdown from "./MultipleSelectDropdown";
import { VueEditor } from "vue2-editor";

export default {
  name: "FormBuilder",
  props: {
    fields: {
      type: Object,
      required: true,
    },
    statusMessageHandler: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      editorToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
    };
  },
  components: {
    GroupFormFields: () => import("./FormBuilder.vue"),
    ToggleButton,
    Dropdown,
    OtpField,
    MultipleSelectDropdown,
    VueEditor,
  },
  methods: {
    OTPChanged(field, otpValue) {
      field.value = otpValue;
      this.fieldChanged();
    },

    fieldChanged(propogateToParent) {
      if (propogateToParent) this.$parent.$emit("fieldChanged");
      else this.$emit("fieldChanged");
    },

    fieldFocused(field, propogateToParent = false) {
      field.hasError = false;
      if (propogateToParent) this.$parent.$emit("fieldFocused");
      else this.$emit("fieldFocused");
    },
  },
};
</script>

<style scoped lang="scss">
.field-item {
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 1rem;
  }
}

.field-title {
  margin-left: 0.15rem;
  margin-bottom: 0.75rem;

  label {
    display: block;
    font-size: $smallFontSize;
    font-weight: $mediumFontWeight;
    color: $greyColor;
  }

  p {
    font-size: $smallerFontSize;
    color: lighten($greyColor, 30%);
    margin-top: 0.25rem;
  }

  &.group-title {
    padding-bottom: 1rem;
    margin-bottom: 2rem;
    border-bottom: 1px solid $greyBorderColor;

    label {
      font-size: $normalFontSize;
      font-weight: $boldFontWeight;
    }
  }
}

.field-item-wrapper {
  &.single-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    .field-title {
      flex: 1;
      width: 60%;
      margin-right: 3rem;
      margin-bottom: 0;
    }

    .field-wrapper {
      width: calc(40% - 3rem);
      min-width: 17.5rem;
    }

    &.field-item-toggle {
      .field-toggle-item {
        float: right;
      }

      .field-wrapper {
        width: auto;
      }
    }
  }
}

.pad-group-fields {
  margin-left: 1.5rem;
}

.alert-message {
  display: table;
  margin-top: 0.75rem;

  ::v-deep .unicon svg {
    width: 1rem;
    height: auto;
    margin-right: 0.5rem;
    margin-top: 0.15rem;
  }

  span {
    font-size: $smallerFontSize;
  }

  .alert-html {
    color: $greyColor;
  }

  .alert-notification {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0.25rem 0.75rem;
    border-radius: 0.5rem;

    &.yellow {
      background-color: $warningYellowBackgroundColor;
      border: 1px solid $warningYellowBorderColor;

      span {
        color: $warningYellowColor;
      }

      ::v-deep .unicon svg {
        fill: $warningYellowColor;
        height: auto;
        width: 0.85rem;
      }
    }

    &.green {
      background-color: $greenMessageColor;
      border: 1px solid $greenMessageBorderColor;

      span {
        color: $greenMessageTextColor;
      }

      ::v-deep .unicon svg {
        fill: $greenMessageTextColor;
        height: auto;
        width: 0.85rem;
      }
    }
  }
}

.tiny-field {
  padding: 0.5rem 0.75rem;
  font-size: $smallerFontSize;

  &::placeholder {
    font-size: $smallerFontSize;
  }
}

.type-text {
  position: relative;
}

.prefix {
  position: absolute;
  top: 0.25rem;
  left: 0.25rem;
  height: calc(100% - 0.5rem);
  font-size: $smallerFontSize;
  text-align: center;
  background: darken($whiteColor, 10%);
  color: darken($whiteColor, 65%);
  border-radius: 0.35rem;
  line-height: 2.25rem;
  font-weight: $mediumFontWeight;
}
</style>
